import { useEffect, useState } from "react";
import { backgroundActionsUrl } from "../config";
import { getAuthHeader } from "../customAuth/authContext";

interface DashboardViewProps {
    endpoint: string;
    title?: string;
    autoRefresh?: boolean;
    refreshInterval?: number;
}

const DashboardView = ({
    endpoint,
    title,
    autoRefresh = false,
    refreshInterval = 30000
}: DashboardViewProps) => {
    const [htmlContent, setHtmlContent] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const loadDashboard = async () => {
        if (!autoRefresh) setLoading(true);
        setError('');

        try {
            const response = await fetch(`${backgroundActionsUrl}/${endpoint}`, {
                method: 'GET',
                headers: {
                    'Authorization': getAuthHeader(),
                    'Accept': 'text/html'
                }
            });

            if (response.ok) {
                const content = await response.text();
                setHtmlContent(content);
            } else {
                setError(`Failed to load content. Status: ${response.status}`);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
            setError('Failed to load dashboard. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDashboard();
    }, [endpoint]);

    // Auto-refresh functionality
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (autoRefresh) {
            interval = setInterval(() => {
                loadDashboard();
            }, refreshInterval);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, refreshInterval]);

    if (loading && !htmlContent) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px'
            }}>
                <div>Loading {title || 'dashboard'}...</div>
            </div>
        );
    }

    if (error && !htmlContent) {
        return (
            <div style={{
                padding: '20px',
                color: 'red',
                textAlign: 'center'
            }}>
                {error}
            </div>
        );
    }

    return (
        <div style={{
            width: '100%',
            height: "calc(100vh - 110px)",
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header with title and controls */}
            {title && (
                <div style={{
                    padding: '15px 20px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0, color: '#495057' }}>{title}</h3>
                    <button
                        onClick={loadDashboard}
                        disabled={loading}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: loading ? '#6c757d' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>
                </div>
            )}

            <iframe
                srcDoc={htmlContent}
                style={{
                    width: '100%',
                    height: title ? 'calc(100vh - 80px)' : '100vh',
                    border: 'none',
                    flex: 1
                }}
                title={title || 'Dashboard'}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
        </div>
    );
};

// Keep the old export for backward compatibility
export const BackgroundJobsView = (props: { location: string }) => (
    <DashboardView endpoint={props.location} />
);

export default DashboardView;